const SUPPLY_REVIEW_TEMPLATE_ID = "aht-weight-management-4week-review";
const SIX_MONTH_REVIEW_TEMPLATE_ID = "aht-weight-management-6month-review";
const WEEKLY_QUESTIONNAIRE_DAYS_BEFORE = 3;
const MONTHLY_QUESTIONNAIRE_DAYS_BEFORE = 7;

export interface GenerateAHTAppointmentsInput {
  initialSupplyDate: string;
  drugId: "tirzepatide" | "semaglutide" | "oral_semaglutide" | "liraglutide" | "mysimba" | "orlistat";
  sectionBReviewDays: number;
  drugSpecificReviewDays: number;
  drugSpecificReviewLabel: string;
}

export interface AHTPlannedAppointment {
  id: string;
  date: string;
  serviceType: string;
  requiresVideoOrF2F: boolean;
  preConsultationTemplate: string;
  preConsultationSendValue: string;
  preConsultationSendUnit: "days";
  isTitrationAppointment: boolean;
}

type LabelSuffix = "none" | "drugSpecific" | "sixMonth" | "both";

interface AppointmentSpec {
  day: number;
  questionnaireDaysBefore: number;
  isTitrationAppointment: boolean;
}

const TIRZEPATIDE_SCHEDULE: AppointmentSpec[] = [
  { day: 28,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 56,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 84,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 112, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 140, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 168, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
];

const LIRAGLUTIDE_SCHEDULE: AppointmentSpec[] = [
  { day: 7,   questionnaireDaysBefore: WEEKLY_QUESTIONNAIRE_DAYS_BEFORE,  isTitrationAppointment: true  },
  { day: 14,  questionnaireDaysBefore: WEEKLY_QUESTIONNAIRE_DAYS_BEFORE,  isTitrationAppointment: true  },
  { day: 21,  questionnaireDaysBefore: WEEKLY_QUESTIONNAIRE_DAYS_BEFORE,  isTitrationAppointment: true  },
  { day: 28,  questionnaireDaysBefore: WEEKLY_QUESTIONNAIRE_DAYS_BEFORE,  isTitrationAppointment: true  },
  { day: 56,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 84,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 112, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 168, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
];

const MYSIMBA_SCHEDULE: AppointmentSpec[] = [
  { day: 7,   questionnaireDaysBefore: WEEKLY_QUESTIONNAIRE_DAYS_BEFORE,  isTitrationAppointment: true  },
  { day: 14,  questionnaireDaysBefore: WEEKLY_QUESTIONNAIRE_DAYS_BEFORE,  isTitrationAppointment: true  },
  { day: 21,  questionnaireDaysBefore: WEEKLY_QUESTIONNAIRE_DAYS_BEFORE,  isTitrationAppointment: true  },
  { day: 28,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 56,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 84,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 112, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 168, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
];

const ORLISTAT_SCHEDULE: AppointmentSpec[] = [
  { day: 28,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 56,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 84,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 112, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 140, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 168, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
];

const ORAL_SEMAGLUTIDE_SCHEDULE: AppointmentSpec[] = [
  { day: 30,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 60,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 90,  questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 120, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 150, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
  { day: 180, questionnaireDaysBefore: MONTHLY_QUESTIONNAIRE_DAYS_BEFORE, isTitrationAppointment: false },
];

const DRUG_SCHEDULES: Record<GenerateAHTAppointmentsInput["drugId"], AppointmentSpec[]> = {
  tirzepatide: TIRZEPATIDE_SCHEDULE,
  semaglutide: TIRZEPATIDE_SCHEDULE,
  oral_semaglutide: ORAL_SEMAGLUTIDE_SCHEDULE,
  liraglutide: LIRAGLUTIDE_SCHEDULE,
  mysimba:     MYSIMBA_SCHEDULE,
  orlistat:    ORLISTAT_SCHEDULE,
};

function addDays(isoDate: string, days: number): string {
  const date = new Date(isoDate);
  date.setUTCDate(date.getUTCDate() + days);
  const result = date.toISOString().split("T")[0];
  if (result === undefined) {
    throw new Error(`addDays: could not format result for "${isoDate}" + ${days}`);
  }
  return result;
}

function resolveLabel(labelSuffix: LabelSuffix, drugSpecificReviewLabel: string): string {
  switch (labelSuffix) {
    case "none":
      return "AHT Weight Management — Supply Review";
    case "drugSpecific":
      return `AHT Weight Management — Supply Review & ${drugSpecificReviewLabel}`;
    case "sixMonth":
      return "AHT Weight Management — Supply Review & 6-Month Review";
    case "both":
      return `AHT Weight Management — Supply Review, ${drugSpecificReviewLabel} & 6-Month Review`;
  }
}

function makeAppointment(
  date: string,
  serviceType: string,
  template: string,
  questionnaireDaysBefore: number,
  isTitrationAppointment: boolean,
): AHTPlannedAppointment {
  return {
    id: crypto.randomUUID(),
    date,
    serviceType,
    requiresVideoOrF2F: template === SIX_MONTH_REVIEW_TEMPLATE_ID,
    preConsultationTemplate: template,
    preConsultationSendValue: String(questionnaireDaysBefore),
    preConsultationSendUnit: "days",
    isTitrationAppointment,
  };
}

export function generateAHTAppointments(
  input: GenerateAHTAppointmentsInput,
): AHTPlannedAppointment[] {
  const { initialSupplyDate, drugId, drugSpecificReviewDays, sectionBReviewDays, drugSpecificReviewLabel } = input;
  const schedule = DRUG_SCHEDULES[drugId];

  return schedule.map((spec) => {
    const isDrugSpecific = spec.day === drugSpecificReviewDays;
    const isSixMonth = spec.day === sectionBReviewDays;

    const labelSuffix: LabelSuffix =
      isDrugSpecific && isSixMonth ? "both"
      : isDrugSpecific             ? "drugSpecific"
      : isSixMonth                 ? "sixMonth"
      :                              "none";

    const template =
      isDrugSpecific || isSixMonth ? SIX_MONTH_REVIEW_TEMPLATE_ID : SUPPLY_REVIEW_TEMPLATE_ID;

    return makeAppointment(
      addDays(initialSupplyDate, spec.day),
      resolveLabel(labelSuffix, drugSpecificReviewLabel),
      template,
      spec.questionnaireDaysBefore,
      spec.isTitrationAppointment,
    );
  });
}
